/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import Utils from "./Utils";
import Monster from "./Monster"
import Attributes from "./Attributes";
import AttributeValues from "./AttributeValues";
import SkillSet, { Skill } from "./SkillSet";
import Section from "./Section";
import Abilities from "./Abilities";

class Open5e {
    static capitalizeWords = (str: string) => {
        return str && str.replace(/\b\w/g, match => match.toUpperCase());
    }

    static prepAbilityArray = (arry: any[]): Ability[] => {
        const abilities: Ability[] = [];
        // convert any 'description' into 'desc', and make it an Ability object:
        for (const ability of arry || []) {
            abilities.push({
                name:          ability.name,
                desc:          ability.description || ability.desc,
                id:            ability.id,
                label:         ability.label,
                attribute:     ability.attribute,
                group:         ability.group,
                groupOrder:    ability.groupOrder,
                attack_bonus:  ability.attack_bonus,
                damage_dice:   ability.damage_dice,
                damage_bonus:  ability.damage_bonus
            });
        }
        return abilities;
    }

    static translateMonster = (open5eMonster: any): Monster => {
        // add in the default extension fields, in their default state:
        const monster: Monster = new Monster(); //{ ...open5eMonster };

        for (const attr in open5eMonster) {
            if ((typeof open5eMonster[attr] === 'string') && 
                ((open5eMonster[attr].toLowerCase() === 'false') ||
                 (open5eMonster[attr].toLowerCase() === '—'))) {
                delete open5eMonster[attr];
            }
        }

        monster.doc_ref.slug            = open5eMonster.slug;
        monster.desc                    = open5eMonster.desc || open5eMonster.description;
        monster.name                    = open5eMonster.name;
        monster.size                    = Open5e.capitalizeWords(open5eMonster.size);
        monster.type                    = Open5e.capitalizeWords(open5eMonster.type);
        // subrace
        monster.subtype                 = Open5e.capitalizeWords(open5eMonster.subtype);
        monster.group                   = open5eMonster.group;
        monster.alignment               = Open5e.capitalizeWords(open5eMonster.alignment);
        monster.armor_class             = open5eMonster.armor_class;
        monster.armor_desc              = open5eMonster.armor_desc;
        monster.hit_points              = open5eMonster.hit_points;
        monster.hit_dice                = open5eMonster.hit_dice;
        monster.speed                   = open5eMonster.speed;
        monster.attributes.strength     = open5eMonster.strength;
        monster.attributes.dexterity    = open5eMonster.dexterity;
        monster.attributes.constitution = open5eMonster.constitution;
        monster.attributes.intelligence = open5eMonster.intelligence;
        monster.attributes.wisdom       = open5eMonster.wisdom;
        monster.attributes.charisma     = open5eMonster.charisma;
        monster.passivePerception       = open5eMonster.perception;
        monster.damage_vulnerabilities  = open5eMonster.damage_vulnerabilities;
        monster.damage_resistances      = open5eMonster.damage_resistances;
        monster.damage_immunities       = open5eMonster.damage_immunities;
        monster.condition_immunities    = open5eMonster.condition_immunities;
        monster.senses                  = open5eMonster.senses;
        monster.languages               = open5eMonster.languages;
        monster.challenge_rating        = open5eMonster.challenge_rating;
        monster.cr                      = parseFloat(open5eMonster.cr);
        monster.actions                 = Open5e.prepAbilityArray(open5eMonster.actions);
        monster.bonus_actions           = Open5e.prepAbilityArray(open5eMonster.bonus_actions);
        monster.reactions               = Open5e.prepAbilityArray(open5eMonster.reactions);
        monster.legendary_desc          = open5eMonster.legendary_desc;
        monster.legendary_actions       = Open5e.prepAbilityArray(open5eMonster.legendary_actions);
        monster.traits                  = Open5e.prepAbilityArray(open5eMonster.special_abilities || open5eMonster.traits);
        monster.spell_list              = open5eMonster.spell_list;
        monster.environments            = open5eMonster.environments;
        monster.img_main                = open5eMonster.img_main;
        monster.doc_ref.page_no                 = open5eMonster.page_no;
        monster.doc_ref.document__slug          = open5eMonster.document__slug;
        monster.doc_ref.document__title         = open5eMonster.document__title;
        monster.doc_ref.document__license_url   = open5eMonster.document__license_url;
        monster.doc_ref.document__url           = open5eMonster.document__url;

        monster.proficiencyBonus = Utils.getProfBonusByCR(monster.cr);
        monster.xp = Utils.getXpByCR(monster.cr);

        // Parse the hit dice string using regular expressions
        const hitDiceMatch = monster.hit_dice?.match(/^(\d+)d *(\d+) *([-+] *\d+)?$/);
        if (hitDiceMatch) {
            // Extract the hit dice components
            monster.hitDiceNumber = parseInt(hitDiceMatch[1]) || monster.hitDiceNumber || 1;
            monster.hitDiceType = parseInt(hitDiceMatch[2]) || monster.hitDiceType || 8;
            monster.hitDiceBonus = hitDiceMatch[3] ? parseInt(hitDiceMatch[3].replace(/ /g, '')) : (monster.hitDiceBonus || 0);
            monster.usePcHitDice = open5eMonster.usePcHitDice ?? false;
            monster.useMm2024    = open5eMonster.useMm2024    ?? false;
            monster.hitPointsCustom = `${monster.hit_points} (${monster.hit_dice})`;
        } else {
            if (monster.hit_dice) {
                console.error(`Invalid hit dice format, but still using: ${monster.hit_dice}`);
                monster.hitPointsCustom = monster.hit_dice;
            } else {
                // missing hit dice, using hit_points instead
                monster.hitPointsCustom = '' + monster.hit_points;
            }
        }

        if (monster.senses === '-') {
            monster.senses = '';
        }
        if (monster.senses) {
            const senseMatch = monster.senses?.toLowerCase().match(/.*passive perception (\d+).*/);
            if (senseMatch) {
                monster.passivePerception = parseInt(senseMatch[1]);
            }
        }

        const saving_throws = [];
        for (const attr of Attributes.attributes) {
            const saveKey = attr + '_save';
            if (saveKey in open5eMonster) {
                let attrSave = open5eMonster[saveKey];
                if (attrSave != null) {
                    const attrLevel = open5eMonster[attr];
                    const defaultValue = Attributes.getBonus(attrLevel);
                    if (defaultValue != attrSave) {
                        if (attrSave > 0) {
                            attrSave = "+" + attrSave;
                        }
                        saving_throws.push(attr.substring(0, 3).toUpperCase() + " " + attrSave);
                    }
                }
            }
        }
        monster.saving_throws = saving_throws.join(', ');

        // read in the skills Records
        const skillsObj = open5eMonster.skills instanceof Map
            ? Object.fromEntries(open5eMonster.skills)
            : open5eMonster.skills;
        monster.skillSet = SkillSet.parseFromObject(skillsObj, monster.attributes, monster.proficiencyBonus);
        
        if (monster.desc) {
            if (!monster.traits) {
                monster.traits = [];
            }
            monster.traits.unshift({ name: '', desc: monster.desc });
        }
        // const newSpellList = [];
        // for (const spell of monster.spell_list) {
        //   if (spell.startsWith("https://api.open5e.com/v1/spells")) {
        //     const spellObj = await loadDataFromURL(spell);
        //     newSpellList.push(spellObj);
        //   } else {
        //     newSpellList.push({name:'spell', desc: spell});
        //   }
        // }
        // monster.spell_list = newSpellList;
        this.updateAbilities(monster);

        if (open5eMonster.img_main && open5eMonster.img_main != 'http://api.open5e.com/') {
            //Monster.loadImage(jsonObject.img_main).then((imageData) => monster.image.src = imageData);
            monster.image.src            = open5eMonster.img_main;
            monster.image.width          = 400;
            monster.image.opacity        = 1.0;
            monster.image.positionRight  = -400;
            monster.image.positionBottom = 0;
            monster.image.rotation       = 0;
            monster.image.horizontalFlip = false;
            monster.image.clip           = false;
        }
        if (!monster.gear) {
            const newGear = [];
            const possibleGear = Abilities.getGearNames();
            for (const abilities of [monster.actions, monster.inventory]) {
                for (const ability of abilities || []) {
                    if (possibleGear.includes(ability.name)) {
                        newGear.push(ability.name);
                    }
                }
            }
            monster.gear = newGear.join(', ');
        }

        Open5e.computeSplitPoint(monster);

        return monster;
    }

    static computeSplitPoint = (monster: Monster) => {
        const countLinesInString = (str: string | {name: string, desc: string}): number => {
            if (typeof str !== 'string') {
                return countLinesInString(str.name + ". " + str.desc);
            }
            if (str.includes('\n')) {
                const paragraphs = str.split('\n');
                let lines = 0;
                paragraphs.forEach(paragraph => {
                    lines += countLinesInString(paragraph);
                });
                return lines;
            }
            // assume 66 characters per line, unless the lines are indented
            let charsPerLine = 66;
            if (str.startsWith('>')) charsPerLine -= 10;
            if (str.startsWith('>>')) charsPerLine -= 10;
            const lines = Math.floor(str.length / charsPerLine) + 1;
            return lines;
        }

        // Deteremine the best split point:
        const getSectionParagraphs = (array: (Ability | string) [], preText?: string, postText?: string): (Ability | string)[] => {
            if (!array || array.length == 0) {
                array = [];
            }
            const results: (Ability | string)[] = [...array];
            if (preText) {
                results.unshift(preText);
            }
            if (postText) {
                results.push(postText);
            }
            if (results.length) {
                const newRow = 'space\nheader\nheader\nline\n';
                if (typeof results[0] === 'string') {
                    results[0] = newRow + results[0];
                } else {
                    results[0] = newRow + results[0].name + ':' + results[0].desc;
                }
            }
            return results;
        }
        const paragraphs = [monster.desc,
                            ...(monster.traits ? ([...monster.traits]) : []),
                            ...getSectionParagraphs(monster.actions      ),
                            ...getSectionParagraphs(monster.spells       ),
                            ...getSectionParagraphs(monster.bonus_actions),
                            ...getSectionParagraphs(monster.reactions    ),
                            ...getSectionParagraphs(monster.inventory    ),
                            ...getSectionParagraphs(monster.legendary_actions, monster.legendary_desc),
                            ...getSectionParagraphs(monster.mythic_actions,    monster.mythic_desc   ),
                            ...getSectionParagraphs(monster.regional_actions,  monster.regional_desc, monster.regional_desc_end),
                            ...getSectionParagraphs(monster.lair_actions,      monster.lair_desc,     monster.lair_desc_end    )
                        ].filter(o => o);
        const linesOnLeftBySplitPoint = [];
        linesOnLeftBySplitPoint[0] = 17;
        if (monster.skillSet.skills.size) linesOnLeftBySplitPoint[0]++;
        if (monster.damage_vulnerabilities) linesOnLeftBySplitPoint[0]++;
        if (monster.damage_immunities) linesOnLeftBySplitPoint[0]++;
        if (monster.condition_immunities) linesOnLeftBySplitPoint[0]++;
        if (monster.languages) linesOnLeftBySplitPoint[0]++;

        for (let split = 1; split <= paragraphs.length; split++) {
            linesOnLeftBySplitPoint[split] = linesOnLeftBySplitPoint[split - 1] + countLinesInString(paragraphs[split - 1]);
        }
        const totalLines = linesOnLeftBySplitPoint[paragraphs.length];
        const halfLines = Math.floor(totalLines / 2);
        monster.splitPoint = 0;
        for (let split = 1; split < paragraphs.length; split++) {
            const deltaCurrent = Math.abs(linesOnLeftBySplitPoint[monster.splitPoint] - halfLines);
            const deltaNext = Math.abs(linesOnLeftBySplitPoint[split] - halfLines);
            if (deltaNext < deltaCurrent) {
                monster.splitPoint = split;
            } else {
                break;
            }
        }
    }

    static checkForNameInDescs = (mon: any, name: string, arrays: any) => {
        // Use a capturing group to capture the target word
        const pattern = new RegExp(`\\bthe\\s(${name})\\b`, 'i');
        for (const ary of arrays) {
            for (const ability of ary || []) {
                const match = ability.desc.match(pattern);
                if (match) {
                    // match[1] contains the first captured group, which is your target word in its original case
                    return match[1];
                }
            }
        }
    }

    static updateAbilities = (mon: Monster) => {
        const arrays: (Ability | null)[][] = [mon.actions,
                                     mon.bonus_actions,
                                     mon.reactions,
                                     mon.legendary_actions,
                                     mon.traits,
                                     mon.inventory,
                                     mon.mythic_actions,
                                     mon.regional_actions,
                                     mon.lair_actions];
        const strings: {val:string, setter:(newVal:string)=>{}}[] = [
            {val: mon.legendary_desc, setter:(newVal) => mon.legendary_desc = newVal},
            {val: mon.mythic_desc,        setter:(newVal) => mon.mythic_desc       = newVal},
            {val: mon.regional_desc,      setter:(newVal) => mon.regional_desc     = newVal},
            {val: mon.regional_desc_end,  setter:(newVal) => mon.regional_desc_end = newVal},
            {val: mon.lair_desc,          setter:(newVal) => mon.lair_desc         = newVal},
            {val: mon.lair_desc_end,      setter:(newVal) => mon.lair_desc_end     = newVal},
        ];
        if (!mon.shortName) {
            let shortName = Open5e.checkForNameInDescs(mon, mon.name, arrays);
            if (!shortName && (mon.name.includes(' ') || mon.name.includes('\u00A0'))) {
                const monWords = mon.name.split(/\s\(\)/);
                const lastWord = monWords[monWords.length - 1];
                shortName = Open5e.checkForNameInDescs(mon, lastWord, arrays);
            }
            if (shortName) {
                mon.shortName = shortName;
                mon.pluralName = shortName + 's';
            }
        }
        for (const abilityArray of arrays) {
            for (const ability of abilityArray ?? []) {
                if (ability) {
                    this.importAbility(ability, mon);
                }
            }
        }
        for (const str of strings) {
            const obj = { name: '', desc: str.val };
            this.importAbility(obj, mon);
            str.setter(obj.desc);
        }
        mon.spells = mon.traits.filter(ability => ability.name.toLowerCase().includes("spellcasting"));
        mon.traits = mon.traits.filter(ability => !ability.name.toLowerCase().includes("spellcasting"));
    }

    static importAbility = (ability: Ability, mon: Monster) => {
        if (Array.isArray(ability.desc)) {
            ability.desc = ability.desc.join("\n");
        }
        ability.name = ability.name?.trim() ?? '';
        ability.desc = ability.desc?.trim() ?? '';
        const name = mon.shortName || mon.name;
        this.handleName(ability, name);
        this.handleSpellCasting(ability, mon);
        this.italicizeAttackPhrase(ability, mon);
        this.removeUnderscoresIfOneGroupFromAbility(ability);
    }
    static removeUnderscoresIfOneGroupFromAbility(ability: Ability): void {
        ability.desc = ability.desc?.split("\n")
                                    .map(line => Open5e.removeUnderscoresIfOneGroup(line))
                                    .join("\n");
    }
    static removeUnderscoresIfOneGroup(line: string): string {
            // Match all groups of consecutive underscores
        const underscoreGroups = line.match(/( *_ *)+/g);
        
        // Only remove underscores if there is exactly one group of them
        if (underscoreGroups && underscoreGroups.length === 1) {
            return line.replace(/( *_ *)+/g, ' ');
        }
        return line;
    }

    static handleName = (ability: Ability, name: string) => {
        this.replaceString(ability, name + 's', '[MONS]');
        this.replaceString(ability, name, '[MON]');
    }
    static replaceString = (ability: Ability, name: string, replacement: string) => {
        // Escape special characters in the monster's name to use in a regular expression
        const escapedMonName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Create a regular expression that matches the monster's name as a whole word
        const regex = new RegExp(`\\b${escapedMonName}\\b`, 'gi');
        // Replace all matches in the description with [MON]
        ability.desc = ability.desc.replace(regex, replacement);
    }

    static handleSpellCasting = (ability: Ability, mon: Monster) => {
        // In case of spellcasting
        if (!ability.name.toLowerCase().includes("spellcasting") || !ability.desc.includes("\n")) {
            return;
        }
        ability.desc = ability.desc.split("\u2022").join("* "); // replace bullet points with '* ', the markdown will convert back to bullet.
        const spellcastingAbility: string  = ability.desc.toLowerCase().includes("ability is intelligence") ? "Intelligence" :
                                                ability.desc.toLowerCase().includes("ability is wisdom") ? "Wisdom" :
                                                ability.desc.toLowerCase().includes("ability is charisma") ? "Charisma" : '';

        if (spellcastingAbility != '') {
            ability.attribute = spellcastingAbility;
            const attributeLevel = parseInt(mon.attributes[spellcastingAbility.toLowerCase() as keyof Attributes]);
            if (attributeLevel) {
                const bonus = Attributes.getBonus(attributeLevel) + mon.proficiencyBonus;
                const dc = 'DC ' + (8 + bonus);
                const toHit = (bonus >= 0 ? '\\+' : '') + bonus + ' to hit';
                const abilityShort = spellcastingAbility.substring(0, 3).toUpperCase();
                // Use replace with global regular expressions
                const dcRegex = new RegExp(dc, 'g');
                const toHitRegex = new RegExp(toHit, 'g');
                const spellSave = `DC [${abilityShort} SAVE]`;
                const spellAttack = `[${abilityShort} ATK] to hit`;

                ability.desc = ability.desc.replace(dcRegex, spellSave)
                                            .replace(toHitRegex, spellAttack)
                                            .replace(`${spellSave}\n ${spellAttack}`, spellSave + ' ' + spellAttack);
            }
        }

        // For hag covens
        let postDesc = "";
        if (ability.name.toLowerCase().includes("shared spellcasting")) {
            const lastLineBreak = ability.desc.lastIndexOf("\n\n");
            postDesc = ability.desc.substring(lastLineBreak).trim();
            ability.desc = ability.desc.substring(0, lastLineBreak);
        }

        const firstLineBreak = ability.desc.indexOf("\n");
        const spellcastingDesc = ability.desc.substring(0, firstLineBreak).trim();
        const spellcastingSpells = ability.desc.substring(firstLineBreak).trim();

        const spellsArr = spellcastingSpells.split("\n");
        for (let index = 0; index < spellsArr.length; index++) {
            const colonLoc = spellsArr[index].indexOf(':');
            if (colonLoc > 0) {
                const spaceAfterColonLoc = spellsArr[index].indexOf(' ', colonLoc);
                if (spaceAfterColonLoc > 0) {
                    let header = spellsArr[index].substring(0, spaceAfterColonLoc).trim();
                    let body = spellsArr[index].substring(spaceAfterColonLoc).trim();
                    // make the body (the list of spells) in italic
                    body = " _" + body + "_";

                    // Anything in parenthesis should be un-italicized:
                    body = body.split("(").join("_(");
                    body = body.split(")").join(")_");
                    if (!header.startsWith('* ') && !header.startsWith('- ')) {
                        header = '* ' + header;
                    }
                    spellsArr[index] = header + body;
                }
            }
        }

        ability.desc = spellcastingDesc + "\n" + spellsArr.join("\n");

        // For hag covens
        if (postDesc.length > 0) {
            ability.desc += "\n\n" + postDesc;
        }
        ability.desc = Section.convertSpellsToTable(ability.desc);
    }

    static italicizeAttackPhrase = (ability: Ability, mon: Monster) => {
        const allAttrByModifier = new Map<number, Set<keyof AttributeValues>>();
        for (const attr of Attributes.attributes) {
            const attLevel: number = mon.attributes[attr];
            const modifier: number = Attributes.getBonus(attLevel);
            // Skip 0-modifier attributes: a damage roll with no flat bonus shouldn't be inferred
            // as "uses this attribute" just because that attribute happens to have a +0 modifier.
            if (modifier === 0) continue;
            if (!allAttrByModifier.has(modifier)) {
                allAttrByModifier.set(modifier, new Set());
            }
            allAttrByModifier.get(modifier)?.add(attr);
        }
        // this will contain only the entries that have 1 value
        const attrByModifier = new Map<number, keyof AttributeValues>();
        for (const entry of allAttrByModifier.entries()) {
            const attrs: Set<keyof AttributeValues> = entry[1];
            if (attrs.size === 1) {
                const attr: keyof AttributeValues | undefined = attrs.values().next().value;
                if (attr)
                    attrByModifier.set(entry[0], attr);
            }
        }

        let indexOfHit = 100;
        // In case of attacks
        if (ability.desc.toLowerCase().includes(" attack:") ||
            ability.desc.toLowerCase().includes(" attack roll:")) { // 2024 format

            const attackTypes = [
                "melee or ranged weapon attack:",
                "melee or ranged spell attack:",
                "melee weapon attack:",
                "melee spell attack:",
                "ranged weapon attack:",
                "ranged spell attack:",
                "melee attack roll:",            // 2024 format
                "ranged attack roll:",           // 2024 format
                "melee or ranged attack roll:"   // 2024 format
            ];

            // Italicize the correct parts of attack-type actions
            let lowercaseDesc = ability.desc.toLowerCase();
            let attackType = null;
            for (const type of attackTypes) {
                if (lowercaseDesc.startsWith(type) || lowercaseDesc.startsWith("_" + type + "_")) {
                    attackType = type;
                    break;
                }
                if (lowercaseDesc.startsWith(type) || lowercaseDesc.startsWith("_" + type + "_")) {
                    attackType = "_" + type + "_";
                    break;
                }
            }
            if (attackType == null) {
                return;
            }

            if (!attackType.startsWith("_")) {
                ability.desc = "_" + ability.desc.slice(0, attackType.length) + "_" +
                                    ability.desc.slice(attackType.length);
            }
            lowercaseDesc = ability.desc.toLowerCase();
            indexOfHit = lowercaseDesc.indexOf(" hit:");
            if (indexOfHit > 0) {
                // Add one to skip over the space at start of " hit:"
                indexOfHit++;
                ability.desc = ability.desc.slice(0, indexOfHit) + "_" +
                               ability.desc.slice(indexOfHit, indexOfHit + "hit:".length) + "_" +
                               ability.desc.slice(indexOfHit + "hit:".length);
                lowercaseDesc = ability.desc.toLowerCase();
            } else {
                // check for an exist "_hit:_", so we don't add another one (Black Flag monsters come with this format)
                indexOfHit = lowercaseDesc.indexOf(" _hit:_");
            }

            const attrToHitFunc = (mon: Monster, attribute: string) => {
                const toHit = Attributes.getBonus(mon.attributes[attribute.toLowerCase() as keyof AttributeValues])
                            + mon.proficiencyBonus;
                return ((toHit > 0) ? '+' : '') + toHit;
            }
            const toHitRegEx = / ([+-]?\s?\d+)(\s+to hit\b)/i;
            let match = toHitRegEx.exec(ability.desc);
            let prefix = '';
            if (!match) {
                // check for the 2024 format, such as: _Melee Attack Roll:_ +4, reach 5 feet. 4 (1d4 + 2) Slashing damage.
                prefix = ' Attack Roll:_ ';
                const toHitRegEx2024 = /( Attack Roll:_ [+-]?\s?\d+)(, (reach|range))/i;
                match = toHitRegEx2024.exec(ability.desc);
                if (match && match.length > 1) {
                    match[1] = match[1].substring(prefix.length);
                }
            }
            if (match) {
                let attributesToConsider = ['Strength', 'Dexterity'];
                if (attackType.includes('spell attack')) {
                    attributesToConsider = ['Intelligence', 'Wisdom', 'Charisma'];
                }
                const toHit = match[1];
                for (const attr of attributesToConsider) {
                    const attrToHit = attrToHitFunc(mon, attr);
                    if (toHit == attrToHit && (!ability.attribute || (ability.attribute == attr))) {
                        const newToHit = ` [${attr.substring(0, 3).toUpperCase()} ATK]`;
                        ability.attribute = attr;
                        ability.desc = ability.desc.replace(match[0], prefix + newToHit + match[2]);
                        lowercaseDesc = ability.desc.toLowerCase();
                        break;
                    }
                }
            }
        }
        const damageRegEx = / \d+ \((\d+d\d+) ?([+-]? ?\d*)\)( [a-z]* damage)?/i;
        let dmgMatch = damageRegEx.exec(ability.desc);
        // indexOfHit will only be <0 if this is an attack from the 'if' block above, and there was no "hit:" text.
        let hitNeeded = (indexOfHit < 0);
        let attrBonus = ability.attribute ? Attributes.getBonus(mon.attributes[ability.attribute.toLowerCase() as keyof AttributeValues]) : 0;
        while (dmgMatch) {
            const dmgDice = dmgMatch[1];
            const dmgDiceBonus = parseInt((dmgMatch[2] || '0').replace(/ /g, ''));
            if (dmgDiceBonus === 0 && attrBonus !== 0) {
                // This is canned dice, not using the attribute's bonus:
                const newDamage = ` [${dmgDice}]${dmgMatch[3] || ''}`;
                ability.desc = ability.desc.replace(dmgMatch[0], newDamage);
            } else {
                // Decide what (if any) attribute to prefix on this specific damage roll.
                // - If ability.attribute is set AND this damage's bonus exactly matches its modifier,
                //   it's the primary attribute-based damage: prefix with the attribute.
                // - If ability.attribute is set but this damage's bonus DOESN'T match (e.g. secondary
                //   "+2 fire damage" on a STR-based attack), don't prefix — show the raw bonus.
                // - If ability.attribute isn't set, try inference from the unique-modifier map.
                let prefix = '';
                let displayBonus: number = dmgDiceBonus;
                if (ability.attribute && dmgDiceBonus === attrBonus) {
                    prefix = ability.attribute.substring(0, 3).toUpperCase() + ' ';
                    displayBonus = 0;
                } else if (!ability.attribute) {
                    const singleAttr: keyof AttributeValues | undefined = attrByModifier.get(dmgDiceBonus);
                    if (singleAttr) {
                        prefix = singleAttr.substring(0, 3).toUpperCase() + ' ';
                        displayBonus = 0;
                    }
                }
                const bonusAttr = (displayBonus > 0) ? (' + ' + displayBonus) : ((displayBonus < 0) ? (' - ' + -displayBonus) : '');
                let newDamage = ' [' + prefix;
                newDamage += dmgDice + bonusAttr + ']';
                if (hitNeeded && (dmgMatch[3] != null)) {
                    hitNeeded = false;
                    newDamage = ' _Hit:_ ' + newDamage;
                }
                ability.desc = ability.desc.replace(dmgMatch[0], newDamage + (dmgMatch[3] || ''));
            }
            dmgMatch = damageRegEx.exec(ability.desc);
        }
        return ability.desc;
    }

}
export default Open5e;